import cx from "classnames";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

import styles from "./styles.module.css";

const MAX_LINES = 3;
const MIN_HIDDEN_LINES = 2;

type RunCodeChangesStringValueProps = {
  value: string;
};

const RunCodeChangesStringValue = ({ value }: RunCodeChangesStringValueProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const valueRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const element = valueRef.current;

    if (!element) return;

    const update = () => {
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight);

      if (!lineHeight) {
        // Sub-pixel line heights round differently, so a single line can measure 1px taller
        setIsClamped(element.scrollHeight - element.clientHeight > 1);

        return;
      }

      // scrollHeight is the full content height in both states, so this holds once expanded too
      setIsClamped(Math.round(element.scrollHeight / lineHeight) - MAX_LINES >= MIN_HIDDEN_LINES);
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(element);

    return () => observer.disconnect();
  }, [value]);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <span className={styles.wrapper}>
      <span
        ref={valueRef}
        className={cx(styles.string, isClamped && !isExpanded && styles.clamped)}
      >
        &quot;{value}&quot;
      </span>
      {isClamped && (
        <button type="button" className={styles.expand} onClick={handleToggle}>
          {isExpanded ? "show less" : "show more"}
        </button>
      )}
    </span>
  );
};

export default RunCodeChangesStringValue;
